// Etsy kategori ozellikleri duzenleyicisi. Kategori (taksonomi) Etsy'den
// okunur; secenekli ozellik acilir menu (coklu olanlar etiket), sayisal ozellik
// deger + birim. Kaynak rozeti: kural / sablon / AI / elle.
// Taslakta (ListingProp[]) ve sablon varsayilanlarinda (mode="template") kullanilir.

import { useState } from "react";
import { Loader2, Sparkles, X, ChevronDown, ChevronRight } from "lucide-react";
import {
  isEnumerated, makeProp, maxValues,
  type EtsyTaxProp, type ListingProp,
} from "../../lib/etsyProperties";

const SOURCE_LABEL: Record<NonNullable<ListingProp["source"]>, string> = {
  rule: "ürün", template: "şablon", ai: "AI", manual: "elle",
};

export function EtsyPropertiesEditor({
  taxProps, loading, error, value, onChange, variationNames = [], styles, onStylesChange,
  onAutoFill, autoFilling, mode = "listing", collapsible = false, defaultOpen = true,
}: {
  taxProps: EtsyTaxProp[] | undefined;
  loading?: boolean;
  error?: string | null;
  value: ListingProp[];
  onChange: (next: ListingProp[]) => void;
  /** Varyasyon eksenleri: ayni addaki ozellik envanterle yazilir, burada kilitli */
  variationNames?: string[];
  styles?: string[];
  onStylesChange?: (next: string[]) => void;
  onAutoFill?: () => void;
  autoFilling?: boolean;
  /** template: sablon varsayilanlari — sayisal alanlar ve kaynak rozeti gizli */
  mode?: "listing" | "template";
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const list = (taxProps ?? []).filter((p) => mode === "listing" || isEnumerated(p));
  const byId = new Map(value.map((p) => [p.propertyId, p]));
  const filled = list.filter((p) => byId.has(p.property_id)).length;
  const lowerVars = variationNames.map((n) => n.toLowerCase());
  const boundToVariation = (p: EtsyTaxProp) =>
    lowerVars.includes(p.name.toLowerCase()) || (p.display_name ? lowerVars.includes(p.display_name.toLowerCase()) : false)
    || (/colou?r/i.test(p.name) && lowerVars.includes("color"));

  const setProp = (tax: EtsyTaxProp, values: string[], scaleName?: string) => {
    const rest = value.filter((p) => p.propertyId !== tax.property_id);
    const p = values.length ? makeProp(tax, values, "manual", scaleName) : null;
    onChange(p ? [...rest, p] : rest);
  };

  const header = (
    <div className="flex items-center justify-between gap-2">
      <button type="button" onClick={() => collapsible && setOpen((v) => !v)}
        className={`flex items-center gap-1 text-xs font-medium text-gray-700 ${collapsible ? "hover:text-gray-900" : "cursor-default"}`}>
        {collapsible && (open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
        {mode === "template" ? "Etsy özellikleri — varsayılan değerler" : "Etsy özellikleri"}
        {taxProps && <span className="font-normal text-gray-400">({filled}/{list.length} dolu)</span>}
      </button>
      {onAutoFill && taxProps && (
        <button type="button" onClick={onAutoFill} disabled={autoFilling}
          className="flex items-center gap-1 rounded-lg border border-fuchsia-200 px-2 py-1 text-[11px] font-medium text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">
          {autoFilling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          Otomatik doldur
        </button>
      )}
    </div>
  );

  if (loading) return <div>{header}<p className="mt-2 flex items-center gap-1 text-[11px] text-gray-400"><Loader2 className="h-3 w-3 animate-spin" /> Kategori özellikleri alınıyor…</p></div>;
  if (error) return <div>{header}<p className="mt-2 text-[11px] text-red-600">{error}</p></div>;
  if (!taxProps) return <div>{header}<p className="mt-2 text-[11px] text-gray-400">Önce Etsy kategorisi seçin.</p></div>;

  return (
    <div>
      {header}
      {(!collapsible || open) && (
        <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
          {list.map((tax) => {
            const cur = byId.get(tax.property_id);
            const label = tax.display_name || tax.name;
            if (mode === "listing" && boundToVariation(tax)) {
              return (
                <div key={tax.property_id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-gray-400" title="Varyasyon ekseni olarak kullanılıyor; değerleri envanterle gider">varyasyonda</span>
                </div>
              );
            }
            const badge = mode === "listing" && cur?.source && (
              <span className={`rounded px-1 text-[9px] ${cur.source === "ai" ? "bg-fuchsia-50 text-fuchsia-700" : cur.source === "manual" ? "bg-gray-100 text-gray-600" : "bg-emerald-50 text-emerald-700"}`}>
                {SOURCE_LABEL[cur.source]}
              </span>
            );
            if (!isEnumerated(tax)) {
              return (
                <label key={tax.property_id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="flex items-center gap-1 text-gray-600">{label} {badge}</span>
                  <span className="flex items-center gap-1">
                    <input type="number" step="0.1" min="0" value={cur?.values[0] ?? ""}
                      onChange={(e) => setProp(tax, e.target.value ? [e.target.value] : [], tax.scales?.find((s) => s.scale_id === cur?.scaleId)?.display_name)}
                      className="w-16 rounded border border-gray-200 px-1 py-0.5 text-right" />
                    {(tax.scales?.length ?? 0) > 0 && (
                      <select value={cur?.scaleId ?? tax.scales![0].scale_id}
                        onChange={(e) => cur && setProp(tax, cur.values, tax.scales!.find((s) => s.scale_id === Number(e.target.value))?.display_name)}
                        className="rounded border border-gray-200 px-1 py-0.5">
                        {tax.scales!.map((s) => <option key={s.scale_id} value={s.scale_id}>{s.display_name}</option>)}
                      </select>
                    )}
                  </span>
                </label>
              );
            }
            const max = maxValues(tax);
            if (max === 1) {
              return (
                <label key={tax.property_id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="flex items-center gap-1 text-gray-600">{label} {badge}</span>
                  <select value={cur?.values[0] ?? ""} onChange={(e) => setProp(tax, e.target.value ? [e.target.value] : [])}
                    className="w-40 rounded border border-gray-200 px-1 py-0.5">
                    <option value="">—</option>
                    {tax.possible_values!.map((v) => <option key={v.value_id} value={v.name}>{v.name}</option>)}
                  </select>
                </label>
              );
            }
            const selected = cur?.values ?? [];
            return (
              <div key={tax.property_id} className="flex items-start justify-between gap-2 text-[11px]">
                <span className="flex items-center gap-1 pt-0.5 text-gray-600">{label} <span className="text-gray-400">≤{max}</span> {badge}</span>
                <div className="flex w-40 flex-wrap justify-end gap-1">
                  {selected.map((v) => (
                    <span key={v} className="flex items-center gap-0.5 rounded bg-gray-100 px-1 py-0.5">
                      {v}
                      <button type="button" onClick={() => setProp(tax, selected.filter((x) => x !== v))} className="text-gray-400 hover:text-red-500"><X className="h-2.5 w-2.5" /></button>
                    </span>
                  ))}
                  {selected.length < max && (
                    <select value="" onChange={(e) => e.target.value && setProp(tax, [...selected, e.target.value])}
                      className="w-full rounded border border-gray-200 px-1 py-0.5">
                      <option value="">+ ekle</option>
                      {tax.possible_values!.filter((v) => !selected.includes(v.name)).map((v) => <option key={v.value_id} value={v.name}>{v.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            );
          })}
          {mode === "listing" && onStylesChange && (
            <label className="flex items-center justify-between gap-2 text-[11px] sm:col-span-2">
              <span className="text-gray-600" title="Etsy 'styles': en fazla 2 kısa stil (Minimalist, Dainty)">Stil (en fazla 2)</span>
              <StylesInput styles={styles ?? []} onCommit={onStylesChange} />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/** Virgulle ayrilan stiller: yazarken serbest, odak cikinca listeye cevrilir */
function StylesInput({ styles, onCommit }: { styles: string[]; onCommit: (next: string[]) => void }) {
  const joined = styles.join(", ");
  const [text, setText] = useState(joined);
  const [editing, setEditing] = useState(false);
  return (
    <input value={editing ? text : joined}
      onFocus={() => { setText(joined); setEditing(true); }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { setEditing(false); onCommit(text.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 2)); }}
      placeholder="Minimalist, Dainty"
      className="w-60 rounded border border-gray-200 px-1.5 py-0.5" />
  );
}
